// src/components/ribbon/Ribbon.tsx
// Microsoft Office-style ribbon with tab groups and actions

import { useState, useEffect, useRef, Fragment } from 'react';
import {
  Calendar, Users, Home, ClipboardList, UserCheck,
  PlusCircle, Clock, Search, LayoutDashboard, LogOut,
  Activity, Bell, BellOff, BellRing, CalendarClock, History, Settings, Sliders
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../features/auth/auth.store';
import { useSettingsStore } from '../../features/settings/settings.store';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import clsx from 'clsx';

interface RibbonTab {
  id: string;
  label: string;
  icon: React.FC<{ className?: string }>;
  roles: string[];
}

interface RibbonAction {
  id: string;
  label: string;
  icon: React.FC<{ className?: string }>;
  path?: string;
  action?: () => void;
  variant?: 'primary' | 'default' | 'danger';
  group: string;
  roles?: string[]; // if set, only show this action for these roles
  dividerAfter?: boolean; // render a vertical separator after this action
}

const TABS: RibbonTab[] = [
  { id: 'home', label: 'Home', icon: Home, roles: ['Patient', 'Admin', 'Reception', 'Doctor', 'Nurse'] },
  { id: 'appointments', label: 'Appointments', icon: Calendar, roles: ['Patient', 'Admin', 'Reception'] },
  { id: 'reception', label: 'Reception', icon: ClipboardList, roles: ['Admin', 'Reception'] },
  { id: 'doctor-view', label: 'My Patients', icon: UserCheck, roles: ['Doctor'] },
  { id: 'settings', label: 'Settings', icon: Settings, roles: ['Admin'] },
];

const ACTIONS: RibbonAction[] = [
  // Home tab
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/', group: 'home' },

  // Appointments tab
  // Quick Book is listed first so Admin/Reception staff land on it by default when
  // opening the Appointments module. Patients (no access to quick-book) fall through
  // to Book Appointment as their default.
  { id: 'quick-book', label: 'Quick Book', icon: PlusCircle, path: '/reception/quick-book', group: 'appointments', variant: 'primary', roles: ['Admin', 'Reception'] },
  { id: 'book', label: 'Book Appointment', icon: PlusCircle, path: '/book', group: 'appointments', variant: 'primary' },
  { id: 'find-doctor', label: 'Find a Doctor', icon: Search, path: '/doctors', group: 'appointments' },

  // Reception tab
  { id: 'pending', label: 'Pending Approvals', icon: Clock, path: '/reception/pending', group: 'reception', variant: 'primary', dividerAfter: true },
  { id: 'queue', label: "Today's Queue", icon: Activity, path: '/reception/queue', group: 'reception' },
  { id: 'upcoming', label: 'Future Appointments', icon: CalendarClock, path: '/reception/upcoming', group: 'reception', dividerAfter: true },
  { id: 'my-handled', label: 'Handled by Me', icon: ClipboardList, path: '/reception/my-appointments', group: 'reception' },
  { id: 'all-records', label: 'All Records', icon: History, path: '/reception/all', group: 'reception' },

  // Doctor tab
  { id: 'today-patients', label: "Today's Patients", icon: UserCheck, path: '/doctor/today', group: 'doctor-view', variant: 'primary' },
  { id: 'upcoming-patients', label: 'Upcoming', icon: CalendarClock, path: '/doctor/my-patients?tab=upcoming', group: 'doctor-view' },
  { id: 'history-patients', label: 'History', icon: History, path: '/doctor/my-patients?tab=history', group: 'doctor-view' },

  // Settings tab
  { id: 'app-settings', label: 'App Settings', icon: Sliders, path: '/settings', group: 'settings', variant: 'primary' },
  { id: 'manage-doctors', label: 'Manage Doctors', icon: Users, path: '/admin/doctors', group: 'settings' },
  { id: 'manage-users', label: 'Manage Users', icon: UserCheck, path: '/admin/users', group: 'settings' },
];

// Map URL pathname + search to the ribbon tab group that owns it
function deriveTabFromPath(pathname: string, search: string): string | null {
  // First try exact match including query string
  const fullPath = pathname + search;
  for (const action of ACTIONS) {
    if (!action.path) continue;
    if (fullPath === action.path) return action.group;
  }
  // Then try pathname-only match
  for (const action of ACTIONS) {
    if (!action.path) continue;
    const actionPath = action.path.split('?')[0];
    if (pathname === actionPath) return action.group;
  }
  return null;
}

export function Ribbon() {
  const { user, logout } = useAuthStore();
  const hospitalName = useSettingsStore((s) => s.hospitalName);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() =>
    deriveTabFromPath(location.pathname, location.search) ?? 'home'
  );

  // When the URL changes (action click / navigation), sync the tab to match the route
  useEffect(() => {
    const tab = deriveTabFromPath(location.pathname, location.search);
    if (tab) setActiveTab(tab);
  }, [location.pathname, location.search]);

  if (!user) return null;

  const role = user.role;
  const visibleTabs = TABS.filter((t) => t.roles.includes(role));
  const tabActions = ACTIONS.filter((a) => a.group === activeTab && (!a.roles || a.roles.includes(role)));

  const isActivePath = (path?: string) => {
    if (!path) return false;
    const [actionPath, actionSearch] = path.split('?');
    if (location.pathname !== actionPath) return false;
    if (!actionSearch) return true;
    return location.search === `?${actionSearch}`;
  };

  // When an action in the group matches the current route, demote primary buttons to default styling
  const hasActiveAction = tabActions.some(a => isActivePath(a.path));

  // Handle tab click: switch active tab AND navigate to the first action in that tab
  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    const firstAction = ACTIONS.find(
      (a) => a.group === tabId && (!a.roles || a.roles.includes(role)) && a.path
    );
    if (firstAction?.path) navigate(firstAction.path);
  };

  return (
    <div className="ribbon sticky top-0 z-40 bg-white border-b border-slate-200 shadow-ribbon select-none">
      {/* ── App Header Bar ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 h-10 bg-brand-700 text-white">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center">
            <Activity className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-sm font-semibold tracking-wide">{hospitalName}</span>
          <span className="text-xs text-brand-200 ml-1">· Appointment System</span>
        </div>

        {/* Right: user + notifications */}
        <div className="flex items-center gap-3">
          {(role === 'Admin' || role === 'Reception') && <PushBell />}

          {/* User menu */}
          <div className="flex items-center gap-2 pl-3 border-l border-brand-500">
            <div className="text-right">
              <div className="text-xs font-semibold text-white leading-none">{user.name}</div>
              <div className="text-[10px] text-brand-200 mt-0.5">{user.role}</div>
            </div>
            <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold text-white">
              {user.name.charAt(0)}
            </div>
            <button
              onClick={logout}
              className="p-1.5 rounded hover:bg-white/10 transition-colors ml-1"
              title="Log out"
            >
              <LogOut className="w-3.5 h-3.5 text-brand-200" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Tab Bar ─────────────────────────────────────────────────────── */}
      <div className="flex items-end gap-0 px-2 bg-brand-600 pt-1">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={clsx(
                'flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-t-md transition-all duration-150',
                activeTab === tab.id
                  ? 'bg-white text-brand-700 shadow-sm'
                  : 'text-brand-100 hover:text-white hover:bg-white/10'
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Action Toolbar ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 px-3 py-2 bg-white min-h-[52px] flex-wrap">
        {tabActions.length === 0 ? (
          <span className="text-xs text-slate-400 italic">No actions available</span>
        ) : (
          tabActions.map((action) => {
            const Icon = action.icon;
            const isActive = isActivePath(action.path);
            const showPrimary = !isActive && action.variant === 'primary' && !hasActiveAction;
            return (
              <Fragment key={action.id}>
                <button
                  onClick={() => {
                    if (action.path) navigate(action.path);
                    action.action?.();
                  }}
                  className={clsx(
                    'flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-md text-xs font-medium',
                    'transition-all duration-150 min-w-[64px]',
                    isActive && 'bg-brand-50 text-brand-700 ring-1 ring-brand-200',
                    showPrimary
                      ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm'
                      : !isActive
                      ? 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      : ''
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span className="whitespace-nowrap">{action.label}</span>
                </button>
                {action.dividerAfter && (
                  <div className="h-10 w-px bg-slate-200 mx-1.5 self-center" aria-hidden="true" />
                )}
              </Fragment>
            );
          })
        )}

        {/* Quick search — hidden on Reception (each listing page has its own search) */}
        {activeTab !== 'reception' && (
          <>
            {/* Separator */}
            {tabActions.length > 0 && (
              <div className="h-10 w-px bg-slate-200 mx-1" />
            )}

            <div className="flex items-center gap-1.5 ml-auto">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="search"
                  placeholder="Search appointments…"
                  className="input pl-8 py-1.5 text-xs h-8 w-56"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const q = (e.target as HTMLInputElement).value;
                      if (q) navigate(`/appointments/${q}`);
                    }
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Push Notification Bell ────────────────────────────────────────────────────
// Shown only for Admin / Reception in the header bar.
// Clicking subscribes / unsubscribes this device/browser.

function PushBell() {
  const { supported, subscribed, permission, loading, subscribe, unsubscribe } = usePushNotifications();
  const [showTip, setShowTip] = useState(false);
  const tipRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (!supported) return null;

  const handleClick = () => {
    if (subscribed) unsubscribe();
    else subscribe();
  };

  const BellIcon = subscribed ? BellRing : permission === 'denied' ? BellOff : Bell;

  const tooltip = loading
    ? 'Setting up…'
    : permission === 'denied'
    ? 'Notifications blocked — enable in browser settings'
    : subscribed
    ? 'Notifications ON — click to turn off on this device'
    : 'Click to get notified of new appointment requests';

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        disabled={loading || permission === 'denied'}
        onMouseEnter={() => { tipRef.current = setTimeout(() => setShowTip(true), 400); }}
        onMouseLeave={() => { if (tipRef.current) clearTimeout(tipRef.current); setShowTip(false); }}
        className={clsx(
          'relative p-1.5 rounded transition-colors',
          loading || permission === 'denied'
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-white/10 cursor-pointer',
        )}
        aria-label={tooltip}
      >
        <BellIcon className={clsx(
          'w-4 h-4 transition-colors',
          subscribed ? 'text-yellow-300' : 'text-brand-100'
        )} />
        {/* Green dot when subscribed */}
        {subscribed && (
          <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-emerald-400" />
        )}
      </button>

      {/* Tooltip */}
      {showTip && (
        <div className="absolute right-0 top-8 z-50 w-56 bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-xl leading-snug pointer-events-none">
          {tooltip}
        </div>
      )}
    </div>
  );
}
